import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bot, X, Send, Sparkles, Lock } from "lucide-react";
import api from "../../lib/api";
import useTier from "../../hooks/useTier";

/**
 * Floating AI Quote Copilot widget — accessible app-wide.
 * - Pro / Pro+ only (capability gate). Free / Starter sees an upgrade hint.
 * - When the user is on a /quotes/:id page, the conversation is automatically
 *   tied to that quote (richer context).
 */
export default function CopilotWidget() {
  const { t } = useTranslation();
  const { capabilities, tier, loading } = useTier();
  const enabled = !!capabilities?.ai_copilot;
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const location = useLocation();

  // Auto-detect quote_id from /quotes/:id route
  const quoteId = (location.pathname.match(/^\/quotes\/([^/]+)$/) || [])[1] || null;

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Don't render on auth/landing routes
  const hideRoutes = ["/", "/login", "/register", "/verify-email"];
  if (hideRoutes.includes(location.pathname) || loading) return null;

  const seed = enabled && messages.length === 0
    ? [{ role: "assistant", content: quoteId
        ? t("copilot.welcomeOnQuote")
        : t("copilot.welcomeGeneric")
      }]
    : messages;

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { id: `u-${Date.now()}`, role: "user", content: input.trim() };
    setMessages([...seed, userMsg]);
    setInput("");
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/copilot/message", {
        message: userMsg.content,
        session_id: sessionId,
        quote_id: quoteId,
      });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { id: `a-${Date.now()}`, role: "assistant", content: data.reply }]);
    } catch (e) {
      setError(e.response?.data?.detail || t("copilot.error"));
    }
    setSending(false);
  };

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-6 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-2xl hover:shadow-amber-500/40 hover:scale-110 transition-all flex items-center justify-center group"
          data-testid="copilot-fab"
          aria-label={t("copilot.name")}
        >
          <Bot size={24}/>
          {enabled && (
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-slate-900 animate-pulse"/>
          )}
          <span className="absolute right-full mr-3 px-2 py-1 rounded bg-slate-900 text-xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-slate-700 pointer-events-none">
            {t("copilot.name")}
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-6 z-[60] w-[400px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-6rem)] rounded-xl border border-slate-700 bg-slate-950 shadow-2xl flex flex-col overflow-hidden"
          data-testid="copilot-panel"
        >
          <div className="px-4 py-3 border-b border-slate-800 bg-gradient-to-r from-amber-500/10 to-transparent flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                <Bot size={16} className="text-white"/>
              </div>
              <div>
                <div className="font-display font-semibold text-sm text-slate-100">{t("copilot.name")}</div>
                <div className="text-[10px] text-slate-500 flex items-center gap-1">
                  <Sparkles size={9}/> Claude Sonnet 4.5 {quoteId && <span className="text-amber-400">· {t("copilot.onQuote")}</span>}
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
              data-testid="copilot-close"
            ><X size={16}/></button>
          </div>

          {!enabled ? (
            <div className="flex-1 flex flex-col items-center justify-center px-8 text-center" data-testid="copilot-upgrade-hint">
              <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mb-4">
                <Lock size={28} className="text-amber-400"/>
              </div>
              <div className="font-display font-bold text-lg text-slate-100 mb-2">{t("copilot.lockedTitle")}</div>
              <div className="text-sm text-slate-400 mb-5">
                {t("copilot.lockedBody")}
              </div>
              <a
                href="/billing"
                className="px-4 py-2.5 rounded-md bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium"
                data-testid="copilot-upgrade-link"
              >
                {t("copilot.upgradeFrom", { price: tier === "free" ? "19" : "49" })}
              </a>
            </div>
          ) : (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="copilot-messages">
                {seed.map((m, i) => (
                  <MessageBubble key={m.id || `${m.role}-${i}`} role={m.role} content={m.content}/>
                ))}
                {sending && (
                  <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="copilot-typing">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" style={{animationDelay:'0.2s'}}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" style={{animationDelay:'0.4s'}}></span>
                    </div>
                    {t("copilot.typing")}
                  </div>
                )}
              </div>

              {error && <div className="px-4 py-2 text-xs text-red-400 border-t border-red-500/30 bg-red-500/10" data-testid="copilot-error">{error}</div>}

              <div className="border-t border-slate-800 p-3">
                <div className="flex gap-2">
                  <textarea
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                    placeholder={quoteId ? t("copilot.placeholderQuote") : t("copilot.placeholderGeneric")}
                    className="flex-1 px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-sm text-slate-100 placeholder:text-slate-600 focus:border-amber-500 focus:outline-none resize-none"
                    data-testid="copilot-input"
                    disabled={sending}
                  />
                  <button
                    onClick={send}
                    disabled={!input.trim() || sending}
                    className="px-3 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-white disabled:opacity-30 disabled:cursor-not-allowed flex items-center"
                    data-testid="copilot-send"
                  >
                    <Send size={16}/>
                  </button>
                </div>
                {quoteId && (
                  <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
                    <Sparkles size={9} className="text-amber-400"/>
                    {t("copilot.contextActive")}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
          isUser
            ? "bg-amber-500 text-white rounded-br-none"
            : "bg-slate-800 text-slate-100 border border-slate-700 rounded-bl-none"
        }`}
      >
        {content}
      </div>
    </div>
  );
}
